import type { DomainKey } from './derive';
import { levelLabel } from './derive';

const BAND_DESCRIPTORS: Record<DomainKey, Record<number, string>> = {
  listening: {
    1: 'Identifies everyday words, short phrases, and simple directions in spoken English with visual and gestural support.',
    2: 'Follows short spoken directions and identifies key words in simple academic talk with visual support.',
    3: 'Understands the main idea of spoken academic content on familiar topics with repetition and visual scaffolds.',
    4: 'Comprehends extended academic discourse on familiar topics — main ideas, supporting details, and implicit relationships — with limited need for visual scaffolds.',
    5: 'Comprehends complex academic discourse across content areas, including nuance, tone, and implied meaning, with minimal support.',
    6: 'Comprehends and interprets grade-level academic discourse comparable to a proficient English-speaking peer.',
  },
  speaking: {
    1: 'Communicates basic needs and ideas using single words, set phrases, and gestures.',
    2: 'Produces short phrases and memorized expressions on everyday topics with frequent pauses.',
    3: 'Discusses familiar academic topics in connected sentences, using everyday and some academic vocabulary.',
    4: 'Engages in extended oral exchanges on academic topics with varied sentence structures and content-specific vocabulary, with occasional pauses for word retrieval.',
    5: 'Sustains academic discussion across content areas with precise vocabulary and complex sentence structures.',
    6: 'Speaks fluently on grade-level academic topics comparable to a proficient English-speaking peer.',
  },
  reading: {
    1: 'Recognizes letters, high-frequency words, and labeled images in short, illustrated text.',
    2: 'Reads short, illustrated texts on familiar topics, identifying key words and simple facts.',
    3: 'Reads grade-level academic texts on familiar topics, identifying main ideas and key supporting details, with scaffolding for unfamiliar vocabulary.',
    4: "Reads grade-level texts across content areas, identifying main ideas, supporting details, author's purpose, and implied meaning with limited scaffolding.",
    5: 'Reads complex grade-level texts, analyzing argument, inference, and author intent with minimal support.',
    6: 'Reads and analyzes grade-level academic texts comparable to a proficient English-speaking peer.',
  },
  writing: {
    1: 'Labels pictures and copies words and short familiar phrases.',
    2: 'Writes short sentences on familiar topics using high-frequency vocabulary and simple structures.',
    3: 'Produces structured paragraphs on familiar academic topics with clear main idea and supporting details, using a developing range of sentence structures.',
    4: 'Writes multi-paragraph academic texts with organized ideas, supporting evidence, and varied sentence structures.',
    5: 'Produces cohesive academic writing across content areas with precise vocabulary and complex syntax.',
    6: 'Writes grade-level academic texts comparable to a proficient English-speaking peer.',
  },
};

function bandIndex(level: number): number {
  return Math.min(6, Math.max(1, Math.floor(level)));
}

function descriptor(domain: DomainKey, band: number): string {
  return BAND_DESCRIPTORS[domain][band] ?? BAND_DESCRIPTORS[domain][3] ?? '';
}

export type LevelDescriptor = { band: number; label: string; text: string };

export function currentDescriptor(domain: DomainKey, level: number | null): LevelDescriptor | null {
  if (level == null) return null;
  const band = bandIndex(level);
  return { band, label: levelLabel(level), text: descriptor(domain, band) };
}

export function nextDescriptor(domain: DomainKey, level: number | null): LevelDescriptor | null {
  if (level == null) return null;
  const band = Math.min(6, bandIndex(level) + 1);
  return { band, label: levelLabel(band), text: descriptor(domain, band) };
}

const GERUND: Record<string, string> = {
  Identifies: 'identifying',
  Follows: 'following',
  Understands: 'understanding',
  Comprehends: 'comprehending',
  Communicates: 'communicating',
  Produces: 'producing',
  Discusses: 'discussing',
  Engages: 'engaging',
  Sustains: 'sustaining',
  Speaks: 'speaking',
  Recognizes: 'recognizing',
  Reads: 'reading',
  Labels: 'labeling',
  Writes: 'writing',
  Interprets: 'interpreting',
  Analyzes: 'analyzing',
};

function stripPeriod(text: string): string {
  return text.replace(/\.\s*$/, '');
}

function toGerund(word: string): string {
  const key = word.charAt(0).toUpperCase() + word.slice(1);
  return GERUND[key] ?? word.charAt(0).toLowerCase() + word.slice(1);
}

export function currentCapabilityPhrase(domain: DomainKey, level: number | null): string | null {
  const d = currentDescriptor(domain, level);
  return d ? stripPeriod(d.text) : null;
}

export function nextCapabilityPhrase(domain: DomainKey, level: number | null): string | null {
  const cur = currentDescriptor(domain, level);
  const d = nextDescriptor(domain, level);
  if (!d || (cur != null && d.band === cur.band)) return null;
  const words = stripPeriod(d.text).split(' ');
  words[0] = toGerund(words[0] ?? '');
  if (words[1] === 'and' && words[2]) words[2] = toGerund(words[2]);
  return words.join(' ');
}

